import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { firestore, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import AmendmentSection from "./AmendmentSection";

const ContractForm = () => {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    contractId: "",
    contractTitle: "",
    partyOffered: "",
    partyAccepted: "",
    startDate: "",
    endDate: "",
    placeOfContract: "",
    jurisdiction: "",
    offeredBySignature: "",
    acceptedBySignature: "",
    status: "Draft",
    details: "",
    isFrozen: false,
  });
  const [contractPeriod, setContractPeriod] = useState("");

  // Get user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Auto-calculate contract period
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      try {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        if (end < start) {
          setContractPeriod("End date must be after start date");
          return;
        }
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 60) {
          const diffMonths =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());
          setContractPeriod(`~ ${diffMonths} months (${diffDays} days)`);
        } else {
          setContractPeriod(`${diffDays} days`);
        }
      } catch (error) {
        setContractPeriod("Invalid dates");
      }
    } else {
      setContractPeriod("");
    }
  }, [formData.startDate, formData.endDate]);

  // Fetch data on edit
  useEffect(() => {
    if (contractId) {
      setIsEdit(true);
      const fetchContract = async () => {
        setLoading(true);
        const docRef = doc(firestore, "ContractMaster", contractId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const formattedData = {
            ...data,
            startDate: data.startDate?.toDate
              ? data.startDate.toDate().toISOString().split("T")[0]
              : "",
            endDate: data.endDate?.toDate
              ? data.endDate.toDate().toISOString().split("T")[0]
              : "",
            contractId: docSnap.id,
            isFrozen: data.isFrozen || false,
          };
          setFormData(formattedData);
        } else {
          console.error("No such document!");
        }
        setLoading(false);
      };
      fetchContract();
    }
  }, [contractId]);

  // handleChange
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ --- UPDATED HANDLE SUBMIT WITH VALIDATION ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert("You must be logged in.");
      return;
    }

    // 1. Retrieve Tenant ID from Session Storage
    const sessionTenantId = sessionStorage.getItem("tenantId");

    setLoading(true);

    try {
      const dataToSave = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        updatedAt: serverTimestamp(),
        updatedByEmail: user.email,
      };
      delete dataToSave.contractId;

      if (isEdit) {
        // --- Update logic (Existing Contract) ---
        const docRef = doc(firestore, "ContractMaster", contractId);
        await updateDoc(docRef, dataToSave);
      } else {
        // --- Create logic (New Contract) ---

        // 🛑 VALIDATION CHECK: Ensure Tenant ID exists in DB before using it
        if (!sessionTenantId) {
          throw new Error(
            "No Tenant ID found in session. Please log out and log in again."
          );
        }

        // Check if this tenant actually exists in TenantMaster
        // NOTE: Ensure your collection name is exactly "TenantMaster" (case sensitive)
        const tenantRef = doc(firestore, "TenantMaster", sessionTenantId);
        const tenantSnap = await getDoc(tenantRef);

        if (!tenantSnap.exists()) {
          // If we are here, it means the UserProfile has a tenantID that was deleted from TenantMaster
          setLoading(false);
          alert(
            `CRITICAL ERROR: The Tenant ID (${sessionTenantId}) associated with your account does not exist in the database. It may have been deleted. Please contact Super Admin.`
          );
          return;
        }

        // ✅ Proceed if Tenant exists
        const docRef = await addDoc(collection(firestore, "ContractMaster"), {
          ...dataToSave,
          tenantId: sessionTenantId,
          createdAt: serverTimestamp(),
          createdByEmail: user.email,
          createdById: user.uid,
          isFrozen: false,
        });

        await updateDoc(docRef, {
          contractId: docRef.id,
        });

        navigate(`/contracts/edit/${docRef.id}`);
      }

      setLoading(false);
      alert(`Contract successfully ${isEdit ? "updated" : "saved as draft"}!`);
    } catch (error) {
      console.error("Error saving document: ", error);
      alert(`Error: ${error.message}`);
      setLoading(false);
    }
  };
  // ✅ --- END UPDATED HANDLE SUBMIT ---

  // handleFreezeContract
  const handleFreezeContract = async () => {
    if (
      !window.confirm(
        "Are you sure you want to finalize and freeze this contract? Once frozen, the main fields cannot be edited."
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const docRef = doc(firestore, "ContractMaster", contractId);
      await updateDoc(docRef, {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        isFrozen: true,
        status: "Active",
        updatedAt: serverTimestamp(),
        updatedByEmail: user.email,
      });

      setFormData((prev) => ({ ...prev, isFrozen: true, status: "Active" }));
      setLoading(false);
      alert("Contract has been finalized and frozen.");
    } catch (error) {
      console.error("Error freezing contract: ", error);
      alert(`Error: ${error.message}`);
      setLoading(false);
    }
  };

  if (loading && isEdit) return <CircularProgress sx={{ m: 4 }} />;

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {isEdit ? "Edit Contract" : "Create New Contract"}
        </Typography>

        {formData.isFrozen && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This contract is finalized and frozen. Main fields cannot be edited.
            You can add amendments below.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {isEdit && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  disabled
                  id="contractId"
                  name="contractId"
                  label="Contract ID"
                  value={formData.contractId}
                  InputLabelProps={{ shrink: true }}
                  sx={{ backgroundColor: "#f5f5f5" }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="contractTitle"
                name="contractTitle"
                label="1. Contract Title"
                value={formData.contractTitle}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="partyOffered"
                name="partyOffered"
                label="2. Party (Offered By)"
                value={formData.partyOffered}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="partyAccepted"
                name="partyAccepted"
                label="2. Party (Accepted By)"
                value={formData.partyAccepted}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="startDate"
                name="startDate"
                label="3. Contract Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.startDate}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="endDate"
                name="endDate"
                label="4. Contract End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.endDate}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="contractPeriod"
                name="contractPeriod"
                label="5. Period of contract"
                value={contractPeriod}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  label="Status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={formData.isFrozen}
                >
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="placeOfContract"
                name="placeOfContract"
                label="8. Place of Contract"
                value={formData.placeOfContract}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="jurisdiction"
                name="jurisdiction"
                label="9. Jurisdiction of Contract"
                value={formData.jurisdiction}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="offeredBySignature"
                name="offeredBySignature"
                label="10. Offered By (Type full name to sign)"
                value={formData.offeredBySignature}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="acceptedBySignature"
                name="acceptedBySignature"
                label="10. Accepted By (Type full name to sign)"
                value={formData.acceptedBySignature}
                onChange={handleChange}
                disabled={formData.isFrozen}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
            >
              {!formData.isFrozen && (
                <>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                  >
                    {loading ? <CircularProgress size={24} /> : "Save as Draft"}
                  </Button>
                  {isEdit && (
                    <Button
                      variant="contained"
                      color="error"
                      disabled={loading}
                      onClick={handleFreezeContract}
                      sx={{ minWidth: 120 }}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Finalize & Freeze"
                      )}
                    </Button>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {formData.isFrozen && contractId && (
        <AmendmentSection contractId={contractId} userEmail={user?.email} />
      )}
    </Box>
  );
};

export default ContractForm;
